"use client";

import React from "react";
import {
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  LoadingSpinner,
} from "@west-tennessee-consulting/ui";
import { ArrowLeft, RefreshCw, HardDrive, FolderPlus, Upload, Search, X } from "lucide-react";
import { Breadcrumb } from "./Breadcrumb";
import { GoogleDriveCacheIndicator } from "../GoogleDriveCacheIndicator";
import type { BreadcrumbItem } from "./Breadcrumb";

interface GoogleDriveBrowserHeaderProps {
  email?: string;
  compactMode?: boolean;
  breadcrumbs: BreadcrumbItem[];
  breadcrumbLoading?: boolean;
  viewMode: "drives" | "files";
  canGoBack: boolean;
  onBack: () => void;
  onBreadcrumbNavigate: (item: BreadcrumbItem) => void;
  onRefresh: () => void;
  isLoading: boolean;
  isRefetching: boolean;
  allowCreateFolder?: boolean;
  allowUpload?: boolean;
  onCreateFolder: () => void;
  onUpload: () => void;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function GoogleDriveBrowserHeader({
  email,
  compactMode = false,
  breadcrumbs,
  breadcrumbLoading = false,
  viewMode,
  canGoBack,
  onBack,
  onBreadcrumbNavigate,
  onRefresh,
  isLoading,
  isRefetching,
  allowCreateFolder = true,
  allowUpload = true,
  onCreateFolder,
  onUpload,
  showSearch = false,
  searchValue = "",
  onSearchChange,
}: GoogleDriveBrowserHeaderProps) {
  return (
    <CardHeader className={compactMode ? "pb-3" : ""}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <HardDrive className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="truncate">Google Drive Browser</span>
            {email && (
              <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                {email}
              </Badge>
            )}
            <GoogleDriveCacheIndicator />
          </CardTitle>
          {!compactMode && (
            <CardDescription className="text-xs sm:text-sm">
              Browse shared drives, folders, and files
            </CardDescription>
          )}
          {email && (
            <div className="sm:hidden text-xs text-muted-foreground mt-1 truncate">{email}</div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {canGoBack && (
            <Button onClick={onBack} variant="outline" size="sm" className="p-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}

          {viewMode === "files" && (
            <>
              {allowCreateFolder && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={onCreateFolder} variant="outline" size="sm" className="p-2">
                        <FolderPlus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Create folder</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {allowUpload && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={onUpload} variant="outline" size="sm" className="p-2">
                        <Upload className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Upload files</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}

          <Button
            onClick={onRefresh}
            variant="outline"
            size="sm"
            className="p-2"
            disabled={isLoading || isRefetching}
          >
            {isLoading || isRefetching ? (
              <LoadingSpinner className="h-4 w-4" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      {breadcrumbs.length > 0 ? (
        <div className="flex items-center gap-2">
          <Breadcrumb items={breadcrumbs} onNavigate={onBreadcrumbNavigate} />
          {breadcrumbLoading && <LoadingSpinner className="h-4 w-4 text-muted-foreground" />}
        </div>
      ) : breadcrumbLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Loading path…</span>
          <LoadingSpinner className="h-4 w-4" />
        </div>
      ) : null}

      {showSearch && onSearchChange && (
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search files in this folder…"
            className="pl-8 pr-8"
          />
          {searchValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1"
              onClick={() => onSearchChange("")}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </CardHeader>
  );
}
